import { AutoComplete, Box, FormField, listItemSelectBuilder, Text } from "@wix/design-system";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useBiNext } from "./../../../bi-context-provider/bi-context-provider.js";
import { EditorBaseUrlContext } from "./../../../editor-base-url/editor-base-url-context.js";
import { LINK_MODAL_ID } from "./../../../link-modal/constants.js";
import { SitePagesFetchContext } from "./../../../site-pages/site-pages-fetch-context.js";
import { removeBaseUrl } from "./../../../url/remove-base-url.js";
import {
  DYNAMIC_ITEM_PAGE_SELECT,
  SITE_PAGES_EMPTY_STATE,
  SITE_PAGES_PAGE_SELECT
} from "../../data-hooks/data-hooks.js";
import { useSitePages } from "./use-site-pages.js";
const SitePages = ({
  autoCompletePopoverProps,
  autoFocus,
  link,
  linkType,
  onSelect,
  onSubmit,
  tooltipProps,
  t
}) => {
  const { baseUrl } = useContext(EditorBaseUrlContext);
  const [hasSubpages, setHasSubpages] = useState(false);
  const logBi = useBiNext({ pluginId: "link", modalId: LINK_MODAL_ID });
  const pagesState = useSitePages(linkType);
  const { sitePublished } = useContext(SitePagesFetchContext);
  const options = useMemo(
    () => pagesState.items.filter((item) => sitePublished || !item.hasSubpages).map(pageToOption(tooltipProps)),
    [pagesState.items, tooltipProps]
  );
  const [selectedOption, setSelectedOption] = useState(() => {
    const linkPath = removeBaseUrl(link.url || "", baseUrl);
    return options.find((option) => option.page.path === linkPath) || null;
  });
  const [value, setValue] = useState(selectedOption?.page.title || "");
  const ref = useRef(null);
  const onEnterPressed = (evt) => {
    if (selectedOption && selectedOption.label === value && !hasSubpages) {
      onSubmit(evt);
    } else if (evt.target.getAttribute("data-hook")?.includes("option")) {
      setTimeout(() => onSubmit(evt));
    }
  };
  if (pagesState.areAllFetched && !pagesState.query && !pagesState.items.length) {
    return /* @__PURE__ */ React.createElement(EmptyState, { linkType, t });
  }
  return /* @__PURE__ */ React.createElement(Box, { direction: "vertical", gap: "SP3" }, /* @__PURE__ */ React.createElement(
    SitePageDropdown,
    {
      t,
      linkType,
      autofocus: autoFocus,
      dataHook: SITE_PAGES_PAGE_SELECT,
      pagesState,
      autoCompletePopoverProps,
      options,
      value,
      selectedOption,
      ref,
      hasSubpages,
      setSelectedOption,
      setValue,
      onEnterPressed,
      onSelect,
      logBi,
      setHasSubpages
    }
  ), selectedOption && hasSubpages && /* @__PURE__ */ React.createElement(
    ItemPagesDropdown,
    {
      prefix: selectedOption.page.path,
      autoCompletePopoverProps,
      tooltipProps,
      t,
      onSelect,
      onSubmit,
      logBi
    }
  ));
};
const EmptyState = ({ linkType, t }) => {
  return /* @__PURE__ */ React.createElement(
    Box,
    {
      dataHook: SITE_PAGES_EMPTY_STATE,
      direction: "vertical",
      margin: "SP4",
      gap: "SP2",
      verticalAlign: "middle",
      textAlign: "center"
    },
    /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "bold" }, t(typeTranslations.EmptyState_Text1[linkType])),
    /* @__PURE__ */ React.createElement(Text, { size: "small", weight: "thin" }, t(typeTranslations.EmptyState_Text2[linkType]))
  );
};
const ItemPagesDropdown = ({
  prefix,
  autoCompletePopoverProps,
  tooltipProps,
  t,
  onSelect,
  onSubmit,
  logBi
}) => {
  const linkType = "dynamicItemPage";
  const pagesState = useSitePages(linkType, prefix);
  const [value, setValue] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const onEnterPressed = (evt) => {
    if (selectedOption && selectedOption.label === value) {
      onSubmit(evt);
    }
  };
  const options = useMemo(
    () => pagesState.items.map(pageToOption(tooltipProps)),
    [pagesState.items, tooltipProps, prefix]
  );
  return /* @__PURE__ */ React.createElement(
    SitePageDropdown,
    {
      t,
      linkType: "dynamicItemPage",
      dataHook: DYNAMIC_ITEM_PAGE_SELECT,
      pagesState,
      autoCompletePopoverProps,
      options,
      value,
      selectedOption,
      setSelectedOption,
      setValue,
      onEnterPressed,
      onSelect,
      logBi
    }
  );
};
const SitePageDropdown = ({
  t,
  linkType,
  autofocus,
  dataHook,
  pagesState,
  autoCompletePopoverProps,
  options,
  value,
  selectedOption,
  ref,
  hasSubpages,
  setSelectedOption,
  setValue,
  onEnterPressed,
  onSelect,
  logBi,
  setHasSubpages
}) => {
  const onChange = (evt) => {
    setSelectedOption(null);
    setValue(evt.target.value);
    pagesState.setQuery(evt.target.value);
    onSelect("", "");
  };
  const onBlur = () => {
    if (!selectedOption) {
      setValue("");
    }
    if (selectedOption && value !== selectedOption.label) {
      setValue(selectedOption.label || "");
    }
  };
  const onSelectOption = (option) => {
    pagesState.setQuery("");
    setSelectedOption(option);
    setValue(option.page.title);
    onSelect(
      linkType === "dynamicPage" ? `/${option.page.path}` : option.page.path,
      option.page.title
    );
    if (hasSubpages) {
      onSelect("", "");
    }
    logBi({ dataHook: SITE_PAGES_PAGE_SELECT, value: option.page.title });
  };
  useEffect(() => {
    if (autofocus) {
      ref?.current?.showOptions();
    }
  }, []);
  useEffect(() => {
    if (setHasSubpages) {
      setHasSubpages(checkForSubpages(selectedOption, linkType, pagesState.items));
    }
  }, [selectedOption, linkType, pagesState.items, setHasSubpages]);
  return /* @__PURE__ */ React.createElement(FormField, { label: t(typeTranslations.AutoComplete_Label[linkType]), labelPlacement: "top" }, /* @__PURE__ */ React.createElement(
    AutoComplete,
    {
      autoFocus: autofocus,
      dataHook,
      dropdownWidth: "auto",
      emptyStateMessage: pagesState.isLoading ? void 0 : /* @__PURE__ */ React.createElement(Text, { skin: "disabled", size: "small" }, pagesState.query ? t("LinkSettings_SitePages_All_NoResults_Query", { query: pagesState.query }) : t(typeTranslations.NoResults_Any[linkType])),
      focusOnSelectedOption: true,
      minWidthPixels: "252px",
      hasMore: pagesState.hasMore || pagesState.isLoading,
      infiniteScroll: true,
      loadMore: pagesState.loadMore ? pagesState.loadMore : () => {
      },
      placeholder: t(typeTranslations.AutoComplete_Placeholder[linkType]),
      popoverProps: autoCompletePopoverProps,
      size: "small",
      textOverflow: "ellipsis",
      options,
      value,
      onChange,
      markedOption: options.some((opt) => opt.id === selectedOption?.id) ? selectedOption?.id : void 0,
      onOptionMarked: (option) => {
        setValue(option?.page?.title);
      },
      onEnterPressed,
      selectedId: selectedOption?.id,
      status: pagesState.error ? "error" : pagesState.isLoading ? "loading" : void 0,
      onBlur,
      onSelect: onSelectOption,
      ref
    }
  ));
};
const typeTranslations = {
  AutoComplete_Label: {
    blogPost: "LinkSettings_SitePages_BlogPost_AutoComplete_Label",
    staticPage: "LinkSettings_SitePages_StaticPage_AutoComplete_Label",
    dynamicPage: "LinkSettings_SitePages_DynamicPage_AutoComplete_Label",
    dynamicItemPage: "LinkSettings_SitePages_DynamicItemPage_AutoComplete_Label"
  },
  AutoComplete_Placeholder: {
    blogPost: "LinkSettings_SitePages_BlogPost_AutoComplete_Placeholder",
    staticPage: "LinkSettings_SitePages_StaticPage_AutoComplete_Placeholder",
    dynamicPage: "LinkSettings_SitePages_DynamicPage_AutoComplete_Placeholder",
    dynamicItemPage: "LinkSettings_SitePages_DynamicItemPage_AutoComplete_Placeholder"
  },
  EmptyState_Text1: {
    blogPost: "LinkSettings_SitePages_BlogPost_EmptyState_Text1",
    staticPage: "LinkSettings_SitePages_StaticPage_EmptyState_Text1",
    dynamicPage: "LinkSettings_SitePages_DynamicPage_EmptyState_Text1",
    dynamicItemPage: "LinkSettings_SitePages_DynamicItemPage_EmptyState_Text1"
  },
  EmptyState_Text2: {
    blogPost: "LinkSettings_SitePages_BlogPost_EmptyState_Text2",
    staticPage: "LinkSettings_SitePages_StaticPage_EmptyState_Text2",
    dynamicPage: "LinkSettings_SitePages_DynamicPage_EmptyState_Text2",
    dynamicItemPage: "LinkSettings_SitePages_DynamicItemPage_EmptyState_Text2"
  },
  NoResults_Any: {
    blogPost: "LinkSettings_SitePages_BlogPost_NoResults_Any",
    staticPage: "LinkSettings_SitePages_StaticPage_NoResults_Any",
    dynamicPage: "LinkSettings_SitePages_DynamicPage_NoResults_Any",
    dynamicItemPage: "LinkSettings_SitePages_DynamicItemPage_NoResults_Any"
  }
};
const pageToOption = (tooltipProps) => (page) => Object.assign(
  listItemSelectBuilder({
    ...page,
    ellipsis: true,
    titleMaxLines: 2,
    id: page.id,
    label: page.title,
    size: "small",
    dataHook: "option",
    subtitle: page.updatedDate?.toLocaleDateString() || page.description || void 0,
    tooltipProps: {
      ...tooltipProps,
      children: page.title,
      enterDelay: 1e3,
      placement: "top"
    }
  }),
  { page }
);
const checkForSubpages = (selectedOption, pageType, items) => {
  if (!selectedOption || pageType !== "dynamicPage" || !items || items.length === 0) {
    return false;
  }
  return items.find((item) => item.id === selectedOption.id)?.hasSubpages || false;
};
export {
  SitePages
};
//# sourceMappingURL=site-pages.js.map
